// src/crm/ugc/RatesTab.tsx
//
// Onglet « Montants à fixer » du programme UGC (migration 0021) : les membres actifs ou en pause
// dont le montant pour 1 000 vues n'est pas encore fixé (needsRate), le plus ancien d'abord, avec
// le champ pour le fixer sur place (RateField) et le lien vers la fiche. Tant qu'il manque, la
// personne voit « En cours d'analyse du montant attribuable » et ses relevés ne se valident pas.
// En dessous, les montants déjà fixés, pour comparer. La confirmation d'un montant fixé s'affiche
// au-dessus de la liste, avec le nom de la personne : sa carte sort de la liste aussitôt.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { needsRate } from '../../../functions/lib/ugc/memberRate'
import { formatEuros } from '../../../functions/lib/ugc/rules'
import { formatDate, MEMBER_STATUS_LABELS } from './format'
import { useUgcMembers } from './hooks'
import { RateField, savedDetails } from './RateField'

export function RatesTab() {
  const members = useUgcMembers()
  // Confirmation du dernier montant fixé : au-dessus de la liste, la carte en sortant aussitôt.
  const [notice, setNotice] = useState<string | null>(null)
  const all = members.data ?? []
  const pending = all.filter((m) => needsRate(m)).sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  const set = all
    .filter((m) => m.rateCentsPerThousand !== null && m.status !== 'left')
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr'))

  return (
    <section className="ugc-tab-panel" aria-label="Montants à fixer">
      <p className="ugc-muted">
        Fixe le montant pour 1 000 vues de chaque personne. Tant qu'il manque, elle voit « En cours d'analyse du montant
        attribuable » dans sa page Gains, sans aucun montant, et ses relevés ne se valident pas. Le premier montant la
        prévient par e-mail ; les vidéos qui l'attendaient ont alors leur montant.
      </p>
      {notice && (
        <p role="status" className="ugc-notice">
          {notice}
        </p>
      )}
      {members.isPending && <p className="ugc-muted">Chargement…</p>}
      {members.isError && <p className="ugc-error">Les membres n'ont pas pu être chargés : {members.error.message}</p>}
      {members.isSuccess && pending.length === 0 && <p className="ugc-empty">Aucun montant à fixer.</p>}
      {pending.length > 0 && (
        <ul className="ugc-cards" aria-label="Montants à fixer">
          {pending.map((m) => (
            <li key={m.id} className="ugc-card">
              <header className="ugc-card-head">
                <h3 className="ugc-card-title">{m.displayName || m.email}</h3>
                <span className="ugc-muted">
                  {MEMBER_STATUS_LABELS[m.status]}, dans le programme depuis le {formatDate(m.createdAt)} ·{' '}
                  <Link className="ugc-link" to={`/crm/contacts/${encodeURIComponent(m.creatorId)}`}>
                    Fiche
                  </Link>
                </span>
              </header>
              <RateField
                member={m}
                compact
                onSaved={({ result, cents, first }) =>
                  setNotice(
                    [
                      `Montant de ${m.displayName || m.email} fixé : ${formatEuros(cents)} pour 1 000 vues.`,
                      ...savedDetails(result, first),
                    ].join(' '),
                  )
                }
              />
            </li>
          ))}
        </ul>
      )}
      {set.length > 0 && (
        <>
          <h2 className="ugc-subtitle">Montants fixés</h2>
          <div className="ugc-table-wrap">
            <table className="ugc-table" aria-label="Montants fixés">
              <thead>
                <tr>
                  <th scope="col">Personne</th>
                  <th scope="col">Pour 1 000 vues</th>
                  <th scope="col">Modifié le</th>
                  <th scope="col">Fiche</th>
                </tr>
              </thead>
              <tbody>
                {set.map((m) => (
                  <tr key={m.id}>
                    <td>{m.displayName || m.email}</td>
                    <td className="ugc-num">{m.rateCentsPerThousand === null ? '' : formatEuros(m.rateCentsPerThousand)}</td>
                    <td>{m.rateSetAt ? formatDate(m.rateSetAt) : ''}</td>
                    <td>
                      <Link className="ugc-link" to={`/crm/contacts/${encodeURIComponent(m.creatorId)}`} aria-label={`Fiche de ${m.displayName}`}>
                        Fiche
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
